import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import * as teams from '@microsoft/teams-js';
import { appConfig } from './generated/appConfig';
import type { HostMode } from './types';

const scope = `${appConfig.apiApplicationIdUri}/access_as_user`;
let mode: HostMode = 'browser';
let msal: PublicClientApplication | undefined;
let account: AccountInfo | undefined;
let authError = '';
let loginStarted = false;

/** Fehler der Anmeldeprüfung beim Start (z. B. abgelehnte Weiterleitung); leer, wenn alles gut ging. */
export function getAuthError(): string { return authError; }

const AUTH_LOG_KEY = 'pv-auth-log';
export interface AuthLogEntry { at: string; event: string; detail?: string }

/** Die letzten Anmelde-Ereignisse dieses Geräts (nur Zeit, Ereignis und Fehlercode, nie Token oder Namen), zur Fehlersuche in den Einstellungen. */
export function getAuthLog(): AuthLogEntry[] {
  try { return JSON.parse(localStorage.getItem(AUTH_LOG_KEY) ?? '[]') as AuthLogEntry[]; } catch { return []; }
}
function logAuth(event: string, detail?: string): void {
  try {
    const entries = [...getAuthLog(), { at: new Date().toISOString(), event, detail }].slice(-20);
    localStorage.setItem(AUTH_LOG_KEY, JSON.stringify(entries));
  } catch { /* Speicher gesperrt: kein Protokoll */ }
}
function errorCode(error: unknown): string {
  const code = (error as { errorCode?: string } | undefined)?.errorCode;
  const text = error instanceof Error ? error.message.replace(/\s+/g, ' ').slice(0, 140) : String(error).slice(0, 140);
  return code ? `${code}: ${text}` : text;
}

const LAST_ACCOUNT_KEY = 'pv-last-account';
interface RememberedAccount { id: string; username: string; name: string }

/** Das Konto der letzten erfolgreichen Anmeldung auf diesem Gerät (nur Kennung, Anmeldename und Anzeigename, keine Token). */
function readRemembered(): RememberedAccount | undefined {
  try {
    const value = JSON.parse(localStorage.getItem(LAST_ACCOUNT_KEY) ?? 'null') as Partial<RememberedAccount> | null;
    return value && typeof value.id === 'string' && typeof value.username === 'string' ? { id: value.id, username: value.username, name: value.name ?? value.username } : undefined;
  } catch { return undefined; }
}
function remember(found: AccountInfo): void {
  try { localStorage.setItem(LAST_ACCOUNT_KEY, JSON.stringify({ id: found.homeAccountId, username: found.username, name: found.name ?? found.username })); } catch { /* Speicher gesperrt: Konto wird nicht gemerkt */ }
}

/** Name des gemerkten Kontos für die Anmeldeseite („Anmelden als …“); leer, wenn auf diesem Gerät noch keins gemerkt ist. */
export function getRememberedAccountName(): string | undefined { return readRemembered()?.name; }

/** Das zu verwendende Konto aus dem Speicher von MSAL: das gemerkte (zuletzt angemeldete); wurde noch keins gemerkt (Gerät von vor dieser Funktion), das erste, das dann gemerkt wird.
 *  Ist das gemerkte Konto nicht mehr im Speicher (abgemeldet), gilt die App als abgemeldet, ein anderes Konto wird nie stillschweigend genommen. */
function pickAccount(): AccountInfo | undefined {
  if (!msal) return undefined;
  const all = msal.getAllAccounts();
  const remembered = readRemembered();
  if (remembered) return all.find(candidate => candidate.homeAccountId === remembered.id);
  const first = all[0];
  if (first) remember(first);
  return first;
}

function withTimeout<T>(promise: Promise<T>, ms: number, what: string): Promise<T> {
  return Promise.race([promise, new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${what} antwortet nicht (nach ${ms / 1000} s).`)), ms))]);
}

/** Hängengebliebene Anmelde-Markierungen von MSAL entfernen, damit ein neuer Anmeldeversuch nicht blockiert wird. */
function clearInteractionState(): void {
  for (const storage of [window.sessionStorage, window.localStorage]) {
    try { Object.keys(storage).filter(key => key.includes('interaction.status')).forEach(key => storage.removeItem(key)); } catch { /* Speicher gesperrt */ }
  }
}

/** Ob MSAL einen Anmeldestatus („interaction.status“) gespeichert hat; fehlt er bei der Rückkehr von Microsoft, wurde der Zwischenstand verworfen. */
function hasInteractionStatus(): boolean {
  return [window.sessionStorage, window.localStorage].some(storage => {
    try { return Object.keys(storage).some(key => key.includes('interaction.status')); } catch { return false; }
  });
}

/** Ob der Knopf „Anmelden“ schon gedrückt wurde und die Weiterleitung zu Microsoft läuft (nicht: ob die Adresse einen Anmeldecode trägt, das tut `isLoginInProgress`). */
export function isSigningIn(): boolean { return loginStarted; }

/** Ob gerade eine Anmeldung läuft (Weiterleitung zu Microsoft gestartet oder Rückkehr mit Code in der Adresse); ein Neuladen der Seite würde ihren Zwischenstand verwerfen. */
export function isLoginInProgress(): boolean {
  return loginStarted || /[#?&](code|error)=/.test(window.location.hash + window.location.search);
}

function isStandalonePwa(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
}

export async function initializeAuth(): Promise<HostMode> {
  try {
    await Promise.race([
      teams.app.initialize(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Kein M365-Host')), 1500))
    ]);
    const context = await teams.app.getContext();
    const host = String(context.app.host.name).toLowerCase();
    mode = host.includes('outlook') ? 'outlook' : 'teams';
    return mode;
  } catch {
    mode = isStandalonePwa() ? 'pwa' : 'browser';
  }

  msal = new PublicClientApplication({
    auth: {
      clientId: appConfig.clientId,
      authority: `https://login.microsoftonline.com/${appConfig.tenantId}`,
      redirectUri: window.location.origin + '/',
      postLogoutRedirectUri: window.location.origin + '/'
    },
    // Der Zwischenstand einer Anmeldung (Status, Zustand, Nonce) liegt auch im dauerhaften Speicher: Dauert die Anmeldung länger (z. B. Freigabe in der Authenticator-App), kann iOS die installierte
    // PWA im Hintergrund beenden; der Sitzungsspeicher (Vorgabe von MSAL) ist dann leer, die Rückkehr von Microsoft findet keinen Anmeldestatus und liefert kein Konto (zweite Anmeldung nötig).
    // `temporaryCacheLocation` ist in MSAL 4 als veraltet markiert (entfällt in der nächsten Hauptversion); dann ist dafür eine andere Lösung nötig.
    cache: { cacheLocation: 'localStorage', temporaryCacheLocation: 'localStorage' }
  });
  try {
    await withTimeout(msal.initialize(), 15000, 'Die Anmeldekomponente');
    const returning = /[#?&](code|error)=/.test(window.location.hash + window.location.search);
    const statusBefore = hasInteractionStatus();
    const redirect = await withTimeout(msal.handleRedirectPromise(), 15000, 'Die Anmeldung');
    account = redirect?.account ?? pickAccount();
    if (redirect?.account) remember(redirect.account);
    logAuth(returning ? 'Rückkehr von Microsoft' : 'Start', `${mode}; ${redirect?.account ? 'Konto aus der Anmeldung' : account ? 'Konto aus dem Speicher' : 'kein Konto'}${returning && !statusBefore ? '; Anmeldestatus fehlte' : ''}`);
  } catch (error) {
    logAuth('Fehler beim Start', errorCode(error));
    // Nicht hängen bleiben: Fehler merken, Anmeldeseite mit Meldung zeigen.
    authError = error instanceof Error ? error.message : String(error);
    clearInteractionState();
    account = pickAccount();
  }
  return mode;
}

/** Angemeldetes Konto mit den App-Rollen aus dem ID-Token (ohne Netzwerkabruf), damit die Reiter sofort erscheinen. Im Teams-/Outlook-Host liefert erst /me die Rollen. */
export function getAccountSummary(): { id: string; name: string; roles: string[] } | undefined {
  if (!account) return undefined;
  const roles = (account.idTokenClaims as { roles?: unknown } | undefined)?.roles;
  return { id: account.homeAccountId, name: account.name ?? account.username, roles: Array.isArray(roles) ? roles.map(String) : [] };
}

export function getHostMode(): HostMode { return mode; }
export function hasPwaOfflineAccess(): boolean { return mode === 'pwa'; }
export function isSignedIn(): boolean { return mode === 'teams' || mode === 'outlook' || Boolean(account); }

/** `otherAccount`: Kontoauswahl von Microsoft zeigen statt das gemerkte Konto vorzuschlagen. */
export async function signIn(otherAccount = false): Promise<void> {
  if (!msal) throw new Error('Anmeldung ist noch nicht initialisiert.');
  if (loginStarted) { logAuth('Anmelden ignoriert', 'Weiterleitung läuft schon'); return; }   // zweites Tippen: ein zweiter Aufruf würde den Zwischenstand des ersten stören
  // Ist auf diesem Gerät schon ein Konto bekannt, wird es vorgeschlagen (keine erneute Kontoauswahl); sonst die Kontoauswahl wie bisher.
  const hint = otherAccount ? undefined : (account ?? pickAccount())?.username ?? readRemembered()?.username;
  loginStarted = true;
  logAuth('Anmelden gedrückt', otherAccount ? 'anderes Konto, mit Kontoauswahl' : hint ? 'mit bekanntem Konto' : 'mit Kontoauswahl');
  try {
    await msal.loginRedirect(hint ? { scopes: [scope], loginHint: hint } : { scopes: [scope], prompt: 'select_account' });
  } catch (error) {
    loginStarted = false;   // Weiterleitung gescheitert: ein neuer Versuch muss möglich sein
    logAuth('Anmelden fehlgeschlagen', errorCode(error));
    throw error;
  }
}

export async function signOut(): Promise<void> {
  if (msal && account) await msal.logoutRedirect({ account });
}

export async function getAccessToken(): Promise<string> {
  if (mode === 'teams' || mode === 'outlook') {
    return teams.authentication.getAuthToken();
  }
  if (!msal) throw new Error('MSAL ist nicht initialisiert.');
  account = account ?? pickAccount();
  if (!account) {
    loginStarted = true;
    logAuth('Weiterleitung zur Anmeldung', 'kein Konto im Speicher');
    await msal.loginRedirect({ scopes: [scope] });
    throw new Error('Weiterleitung zur Anmeldung läuft.');
  }
  try {
    return (await msal.acquireTokenSilent({ account, scopes: [scope] })).accessToken;
  } catch (error) {
    loginStarted = true;
    logAuth('Stille Erneuerung fehlgeschlagen, Weiterleitung zur Anmeldung', errorCode(error));
    await msal.acquireTokenRedirect({ account, scopes: [scope] });
    throw new Error('Weiterleitung zur Anmeldung läuft.');
  }
}
